import { GUID } from "@faro-lotv/foundation";
import { create } from "zustand";
import { PlanarPoint, WorldMapPose } from "./world-map-alignment";
import { GeoPosition, WorldMapStyle } from "./world-map-tiles";

/** Picks needed for an alignment: data point 1, map point 1, data point 2, map point 2 */
export const ALIGNMENT_PICKS_COUNT = 4;

type WorldMapState = {
  /** Dashboard coordinates of a project (null if it has none) and the user's local alignment */
  projectPosition?: {
    projectId: GUID;
    position: GeoPosition | null;
    pose?: WorldMapPose;
  };
  style: WorldMapStyle;
  /** Points picked in the running alignment, undefined when not aligning */
  alignmentPicks?: PlanarPoint[];
  setProjectPosition(projectId: GUID, position: GeoPosition | null): void;
  setStyle(style: WorldMapStyle): void;
  /** Stores the alignment of the current project, or removes it when undefined */
  setPose(pose?: WorldMapPose): void;
  startAlignment(): void;
  addAlignmentPick(point: PlanarPoint): void;
  undoAlignmentPick(): void;
  stopAlignment(): void;
};

// Prototype only: the alignment is kept in this browser and not shared with other users
function storageKey(projectId: GUID): string {
  return `worldMapAlignment:${projectId}`;
}

function isWorldMapPose(value: unknown): value is WorldMapPose {
  return (
    typeof value === "object" &&
    value !== null &&
    "x" in value &&
    "z" in value &&
    "rotation" in value &&
    typeof value.x === "number" &&
    typeof value.z === "number" &&
    typeof value.rotation === "number"
  );
}

function loadPose(projectId: GUID): WorldMapPose | undefined {
  try {
    const pose: unknown = JSON.parse(
      localStorage.getItem(storageKey(projectId)) ?? "null",
    );
    return isWorldMapPose(pose) ? pose : undefined;
  } catch {
    return undefined;
  }
}

/** Store for the world map layer settings */
export const useWorldMapStore = create<WorldMapState>()((set, get) => ({
  style: WorldMapStyle.map,
  setProjectPosition: (projectId, position) =>
    set({
      projectPosition: { projectId, position, pose: loadPose(projectId) },
    }),
  setStyle: (style) => set({ style }),
  setPose: (pose) => {
    const { projectPosition } = get();
    if (!projectPosition) return;
    const key = storageKey(projectPosition.projectId);
    if (pose) localStorage.setItem(key, JSON.stringify(pose));
    else localStorage.removeItem(key);
    set({ projectPosition: { ...projectPosition, pose } });
  },
  startAlignment: () => set({ alignmentPicks: [] }),
  addAlignmentPick: (point) =>
    set(({ alignmentPicks }) =>
      alignmentPicks ? { alignmentPicks: [...alignmentPicks, point] } : {},
    ),
  undoAlignmentPick: () =>
    set(({ alignmentPicks }) => ({
      alignmentPicks: alignmentPicks?.slice(0, -1),
    })),
  stopAlignment: () => set({ alignmentPicks: undefined }),
}));
